import { Box, Drawer, MenuItem, Popover } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { matchPath } from 'react-router-dom';
import ROUTES from '../../../assets/routes';
import { OptionChainViewKey } from '../../../assets/constants';
import { useSelector } from 'react-redux';
import { selectOptionChainDrawerOpen, selectUnderlyingOptionsTradeData } from '../../../selectors/options';
import { useDispatch } from 'react-redux';
import { setOptionChainDrawerOpen } from '../../../reducer/options.reducer';
import OptionChainHeader from '../../../components/optionChain/header'
import { ExtraSmallButton } from '../../../components/custom';
import Clear from '../../../assets/icons/Clear';
import OptionChainTab from '../optionChainTab'
import { createStringDateFromStamp } from '../../../util/tradePage';
import useSelectPrimaryOrUnderlyingSecurity from '../../../hooks/useSelectPrimaryOrUnderlyingSecurity';
import { selectExpandedRows, selectOptionChainTimeInterval } from '../../../selectors/home';
import { setOptionChainExpandedRows } from '../../../reducer/home.reducer';
import { GetOptionChainRequestV2 } from '../../../proto/generated/candle-data-depot-option-chain';
import { selectOptionChainVisibleStrikes } from '../../../selectors/common';
import { fetchOptionChainV2 } from '../../../util/options';
import { OptionChainTableView } from '../fnoHeader/optionsChainSettingsDialog';
import { selectBasketId, selectTradeOrders } from '../../../selectors/strategyBuilder';
import { selectPositions } from '../../../selectors/positions';
import { Security } from '../../../proto/generated/security';
import { getSecurityAsync, getSecurityKey } from '../../../util/security';

type Props = {}

/** Underlying of the trade being built (first leg, else the basket's first position). */
const useTradeUnderlyingSecurity = (enabled: boolean) => {
    const tradeOrders = useSelector(selectTradeOrders)
    const basketId = useSelector(selectBasketId)
    const positions = useSelector(selectPositions)
    const legSecurity = tradeOrders[0]?.security ?? positions.find(p => p.basketId === basketId)?.security
    const legKey = legSecurity ? getSecurityKey(legSecurity.exchange, legSecurity.code) : ''
    const [resolved, setResolved] = useState<{ legKey: string, security?: Security }>({ legKey: '' })

    useEffect(() => {
        if (!enabled || !legKey) return
        let cancelled = false
        getSecurityAsync(legKey)
            .then((sec) => {
                if (!sec) return undefined
                if (!sec.underlyingSecurityCode) return sec
                return getSecurityAsync(getSecurityKey(sec.exchange, sec.underlyingSecurityCode))
            })
            .catch(() => undefined)
            .then((security) => { if (!cancelled) setResolved({ legKey, security }) })
        return () => { cancelled = true }
    }, [enabled, legKey])

    // Pending while the current leg's lookup hasn't settled; a failed lookup settles with no security.
    const pending = !!legKey && resolved.legKey !== legKey
    return { security: pending ? undefined : resolved.security, pending }
}

const OptionChainRightDrawer = (props: Props) => {
    const dispatch = useDispatch()
    const open = useSelector(selectOptionChainDrawerOpen)
    const optionChainViewKey = useMemo(() => {
        if (matchPath(ROUTES.simulator, window.location.pathname)) {
            return OptionChainViewKey.HISTORICAL
        }
        return OptionChainViewKey.LIVE
    }, [window.location.pathname])
    const expandedRows = useSelector(selectExpandedRows(optionChainViewKey))
    const { primaryOrUnderlying: globalSecurity } = useSelectPrimaryOrUnderlyingSecurity()
    const tradeUnderlying = useTradeUnderlyingSecurity(open)
    // Live chain follows the trade's legs (the chart may have moved to another symbol); the simulator stays route-driven.
    const isLive = optionChainViewKey === OptionChainViewKey.LIVE
    const tradeSecurity = isLive ? tradeUnderlying.security : undefined
    // OptionChainTab resets the strategy builder when its underlying changes, so don't mount it on the global symbol first.
    const waitForTradeUnderlying = isLive && tradeUnderlying.pending
    const mainSecurity = tradeSecurity ?? globalSecurity
    const underlyingToken = mainSecurity ? getSecurityKey(mainSecurity.exchange, mainSecurity.code) : ''
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>()
    const noOfVisibleStrikes = useSelector(selectOptionChainVisibleStrikes)
    const timeInterval = useSelector(selectOptionChainTimeInterval(optionChainViewKey, 'extended'))
    const optionExpiriesMenu: {
        label: string
        value: number
    }[] = useMemo(() => {
        const filteredExpiries = (optionExpiries ?? []).filter(d => !d.derived)
        return filteredExpiries?.map(d => ({
            label: createStringDateFromStamp(Number(d.expiration.expiry?.expiry ?? 0)),
            value: d.expiration.expiry?.expiry ?? 0
        }))
    }, [optionExpiries])

    const handleClose = useCallback(() => {
        dispatch(setOptionChainDrawerOpen(false))
    }, [])

    const handleExpiryClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleExpiryChange = useCallback((index: number) => {
        dispatch(setOptionChainExpandedRows({
            expandedRows: [index.toString()],
            optionChainViewKey: optionChainViewKey
        }))
        if (OptionChainViewKey.HISTORICAL === optionChainViewKey) return
        if (
            mainSecurity &&
            optionExpiries &&
            !expandedRows.includes(index.toString())
            // (optionchainData[index].trades.length === 0 || JSON.stringify(timeInterval) !== JSON.stringify(optionchainData[index].timebucket))
        ) {
            const expandedRowsPassed = [index.toString()]
            const payload: GetOptionChainRequestV2 = {
                security: {
                    code: mainSecurity.code,
                    exchange: mainSecurity.exchange
                },
                expiries: [optionExpiriesMenu[index]?.value ?? 0],
                noOfStrikes: noOfVisibleStrikes === 'all' ? -1 : Number(noOfVisibleStrikes),
                requestedFields: [],
                timeBucket: timeInterval,
                sendFutures: true
            }
            fetchOptionChainV2(payload, dispatch, optionExpiries, 'both', true, expandedRowsPassed)
        }
        setAnchorEl(null)
    }, [optionChainViewKey, mainSecurity, optionExpiries, optionExpiriesMenu, noOfVisibleStrikes, timeInterval])

    return (
        <Drawer
            anchor={'right'}
            open={open}
            PaperProps={{
                sx: {
                    width: '45%',
                    minWidth: '800px',
                    borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                },
            }}
            onClose={handleClose}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ padding: '8px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', fontWeight: 500, fontSize: '16px', flexShrink: 0 }}>
                <ExtraSmallButton sx={{ mr: 1}}>
                    <Clear onClick={handleClose} fontSize='small' />
                </ExtraSmallButton>
                {/* <Typography fontSize={'20px'}>Option Chain</Typography> */}
                <ExtraSmallButton
                    sx={{
                        height: '32px',
                        padding: 0,
                        px: 1,
                        color: theme => theme.palette.text.primary,
                        borderColor: (theme) => theme.palette.background.default,
                    }}
                    variant="outlined"
                    color="inherit"
                    onClick={(e) => handleExpiryClick(e)}
                >
                    {/* <Box
                        component="span"
                        sx={{
                            mx: 1
                        }}
                        className="order-buttons"
                    > */}
                        {createStringDateFromStamp(optionExpiriesMenu[Number(expandedRows?.[0])]?.value ?? 0)}
                    {/* </Box> */}
                </ExtraSmallButton>
                <Popover
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                    {
                        optionExpiriesMenu.map((ex, i) =>
                            <MenuItem data-event={'Expiry Changed'}
                                data-event-source={'Trade'}
                                key={ex.value}
                                selected={ex.value === (optionExpiriesMenu[Number(expandedRows?.[0])]?.value ?? 0)}
                                className="font-feature-num"
                                onClick={() => {
                                    handleExpiryChange(i)
                                }}>{ex.label}
                            </MenuItem>
                        )
                    }
                </Popover>
                <OptionChainHeader showFullScreen={false} orderOptionChain />
            </Box>
            {open && !waitForTradeUnderlying ? (
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <OptionChainTab
                        optionChainViewKey={optionChainViewKey}
                        showSingleExpiry
                        selectedOptionChainTableViewProp={OptionChainTableView.NORMAL}
                        disableRowCollapse={true}
                        optionsTradeDataType="extended"
                        fetchEnabled={open}
                        widgetSecurity={tradeSecurity}
                        widgetSecurityKey={tradeSecurity ? underlyingToken : undefined}
                    />
                </Box>
            ) : null}
            </Box>
        </Drawer>
    )
}

export default OptionChainRightDrawer